"use client";

import { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import {
  Calendar,
  ScrollText,
  ShieldCheck,
  Sparkles,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Slider from "@/components/ui/slider";
import { Card, CardContent } from "@/components/ui/card";
import { useTheme } from "@/contexts/theme-context";

type LegalTab = "terms" | "privacy" | "cookies";

type LegalSection = {
  heading: string;
  body: string[];
  bullets?: string[];
};

type LegalDocument = {
  title: string;
  updated: string;
  summary: string;
  keyPoints: string[];
  sections: LegalSection[];
};

const LegalPage = () => {
  const t = useTranslations("legalPage");
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState<LegalTab>("terms");
  const searchParams = useSearchParams();
  const tabParam = searchParams?.get("tab") as LegalTab | null;
  const [prevTabParam, setPrevTabParam] = useState(tabParam);
  // Follow the ?tab= URL param when it changes (e.g. navigating here from a Footer link),
  // without fighting user tab clicks. Adjusted during render instead of an effect.
  if (tabParam !== prevTabParam) {
    setPrevTabParam(tabParam);
    if (tabParam && ["terms", "privacy", "cookies", "license"].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }

  const docs = t.raw("documents") as Record<LegalTab, LegalDocument>;
  const activeDoc = docs[activeTab];
  const tabLabels: Record<LegalTab, string> = {
    terms: t("tabs.terms"),
    privacy: t("tabs.privacy"),
    cookies: t("tabs.cookies"),
  };
  const [analyticsEnabled, setAnalyticsEnabled] = useState<boolean>(() => {
    try {
      if (typeof window === 'undefined') return false;
      const raw = localStorage.getItem('cookie-preferences');
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      return !!parsed.analytics;
    } catch {
      return false;
    }
  });
  const [functionalEnabled, setFunctionalEnabled] = useState<boolean>(() => {
    try {
      if (typeof window === 'undefined') return false;
      const raw = localStorage.getItem('cookie-preferences');
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      return !!parsed.functional;
    } catch {
      return false;
    }
  });
  const [marketingEnabled, setMarketingEnabled] = useState<boolean>(() => {
    try {
      if (typeof window === 'undefined') return false;
      const raw = localStorage.getItem('cookie-preferences');
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      return !!parsed.marketing;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const onPrefs = (e: Event) => {
      try {
        // @ts-expect-error custom event carries detail
        const detail = e?.detail;
        if (detail && typeof detail === 'object') {
          setAnalyticsEnabled(!!detail.analytics);
          setFunctionalEnabled(!!detail.functional);
          setMarketingEnabled(!!detail.marketing);
        } else {
          const raw = localStorage.getItem('cookie-preferences');
          if (raw) {
            const parsed = JSON.parse(raw);
            setAnalyticsEnabled(!!parsed.analytics);
            setFunctionalEnabled(!!parsed.functional);
            setMarketingEnabled(!!parsed.marketing);
          }
        }
      } catch {
        // ignore
      }
    };

    window.addEventListener('cookie-preferences-changed', onPrefs as EventListener);
    return () => window.removeEventListener('cookie-preferences-changed', onPrefs as EventListener);
  }, []);

  const tabs = useMemo(
    () => [
      { id: "terms" as LegalTab, label: tabLabels.terms, icon: ScrollText },
      { id: "privacy" as LegalTab, label: tabLabels.privacy, icon: ShieldCheck },
      { id: "cookies" as LegalTab, label: tabLabels.cookies, icon: Sparkles },
    ],
    [tabLabels.cookies, tabLabels.privacy, tabLabels.terms],
  );

  const tabButtonClass = (id: LegalTab) => {
    const base = "group inline-flex items-center gap-2 rounded-full border px-3 py-2 sm:px-4 sm:py-3 text-sm font-semibold transition-all";
    if (activeTab === id) {
      return isDark
        ? `${base} border-amber-300/60 bg-amber-300/15 text-white shadow-[0_10px_30px_rgba(244,178,58,0.2)]`
        : `${base} border-amber-300/60 bg-amber-300/15 text-amber-700 shadow-[0_8px_20px_rgba(244,178,58,0.12)]`;
    }
    return isDark
      ? `${base} border-white/10 bg-white/5 text-slate-200 hover:border-amber-200/30 hover:bg-amber-100/10 hover:text-white`
      : `${base} border-gray-200 bg-white text-slate-700 hover:border-amber-200/30 hover:bg-amber-50 hover:text-amber-700`;
  };

  const rootClass = isDark
    ? "relative min-h-screen overflow-hidden bg-gradient-to-br from-surface-dark via-[#0d2036] to-[#0f2947] text-slate-50"
    : "relative min-h-screen overflow-hidden bg-gray-50 text-slate-900";

  const overlaySet = isDark
    ? (
        <div className="absolute inset-0 opacity-40">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(244,178,58,0.15),transparent_35%),radial-gradient(circle_at_80%_0%,rgba(110,231,183,0.08),transparent_25%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(120deg,rgba(255,255,255,0.04)_0%,rgba(255,255,255,0.02)_35%,transparent_70%)]" />
        </div>
      )
    : null;

  const panelClass = isDark
    ? "rounded-card border border-white/10 bg-white/5 px-4 py-6 sm:px-6 sm:py-8 md:px-10 shadow-card-dark backdrop-blur-md"
    : "rounded-card border border-gray-200 bg-white px-4 py-6 sm:px-6 sm:py-8 md:px-10 shadow-sm";

  const cardClass = (extra = "") =>
    isDark ? `border-white/10 bg-white/5 backdrop-blur-md ${extra}` : `border-gray-200 bg-white ${extra}`;

  return (
    <div className={rootClass}>
      {overlaySet}

      <div className="relative mx-auto flex max-w-6xl flex-col gap-8 px-4 pb-20 pt-24 md:px-6 lg:px-8 lg:pt-28">
        <div className={panelClass}>
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="space-y-3">
              <p className={isDark ? "text-xs font-semibold uppercase tracking-display text-amber-300" : "text-xs font-semibold uppercase tracking-display text-amber-600"}>
                {t("brand")}
              </p>
              <h1 className={isDark ? "text-4xl font-black leading-tight text-white md:text-5xl" : "text-4xl font-black leading-tight text-slate-900 md:text-5xl"}>
                {t("title")}
              </h1>
              <p className={isDark ? "max-w-3xl text-base text-slate-200 md:text-lg" : "max-w-3xl text-base text-slate-700 md:text-lg"}>
                {t("subtitle")}
              </p>
            </div>
            <Link href="/" className="self-start lg:self-center">
              <Button className="bg-gradient-to-r from-amber-400 to-orange-500 text-black shadow-[0_15px_40px_rgba(244,178,58,0.35)] hover:shadow-[0_20px_50px_rgba(240,124,58,0.4)] normal-case not-italic font-semibold tracking-tight">
                <ExternalLink className="mr-2 h-4 w-4" />
                {t("backHome")}
              </Button>
            </Link>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            {tabs.map((tab) => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={tabButtonClass(tab.id)}>
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <Card className={cardClass()}>
            <CardContent className={`space-y-6 p-6 md:p-8 ${isDark ? "" : "text-slate-900"}`}>
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className={isDark ? "text-xs uppercase tracking-display text-amber-200" : "text-xs uppercase tracking-display text-amber-600"}>
                    {tabLabels[activeTab]}
                  </p>
                  <h2 className={`${isDark ? "text-2xl font-black text-white md:text-3xl" : "text-2xl font-black text-slate-900 md:text-3xl"}`}>
                    {activeDoc.title}
                  </h2>
                </div>
                <div className={isDark ? "flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold text-slate-200" : "flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700"}>
                  <Calendar className={isDark ? "h-4 w-4 text-amber-200" : "h-4 w-4 text-amber-500"} />
                  {t("metaLabel")}: {activeDoc.updated}
                </div>
              </div>

              <p className={isDark ? "text-base text-slate-100/90" : "text-base text-slate-700"}>{activeDoc.summary}</p>

              <div className="space-y-8">
                {activeDoc.sections.map((section) => (
                  <div key={section.heading} className={isDark ? "space-y-3 rounded-2xl border border-white/10 bg-white/5 p-4 md:p-5" : "space-y-3 rounded-2xl border border-gray-200 bg-white p-4 md:p-5"}>
                    <h3 className={isDark ? "text-lg font-bold text-white" : "text-lg font-bold text-slate-900"}>{section.heading}</h3>
                    <div className={isDark ? "space-y-3 text-slate-100/85" : "space-y-3 text-slate-700"}>
                      {section.body.map((paragraph) => (
                        <p key={paragraph}>{paragraph}</p>
                      ))}
                      {section.bullets && (
                        <ul className={isDark ? "list-disc space-y-2 pl-5 text-slate-100/80" : "list-disc space-y-2 pl-5 text-slate-700"}>
                          {section.bullets.map((item) => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="space-y-4">
            <Card className={cardClass("bg-gradient-to-br from-white/10 via-white/5 to-white/0 backdrop-blur-md")}>
              <CardContent className={`space-y-4 p-6 md:p-7 ${isDark ? "" : "text-slate-800"}`}>
                <div className="flex items-center gap-3">
                  <ShieldCheck className={isDark ? "h-5 w-5 text-amber-300" : "h-5 w-5 text-amber-500"} />
                  <h3 className={isDark ? "text-lg font-semibold text-white" : "text-lg font-semibold text-slate-900"}>{t("complaints.title")}</h3>
                </div>
                <p className={isDark ? "text-slate-100/85" : "text-slate-700"}>{t("complaints.description")}</p>
                <div className={isDark ? "rounded-2xl border border-white/10 bg-white/5 p-4" : "rounded-2xl border border-gray-200 bg-white p-4"}>
                  <div className="mx-auto mb-4 w-fit overflow-hidden rounded-2xl bg-white p-3">
                    <Image
                      src="/static/legal/legal-qr.webp"
                      alt={t("complaints.qrAlt")}
                      width={180}
                      height={180}
                      className="h-[180px] w-[180px]"
                    />
                  </div>
                  <a
                    href={t("complaints.linkHref")}
                    target="_blank"
                    rel="noreferrer"
                    className={isDark ? "block text-sm font-semibold text-amber-200 underline decoration-amber-300/50 underline-offset-4 transition hover:text-white" : "block text-sm font-semibold text-amber-700 underline decoration-amber-400/60 underline-offset-4 transition hover:text-amber-800"}
                  >
                    {t("complaints.linkLabel")}
                  </a>
                </div>
              </CardContent>
            </Card>
            <Card className={cardClass()}>
              <CardContent className={`space-y-4 p-6 md:p-7 ${isDark ? "" : "text-slate-800"}`}>
                <div className="flex items-center gap-3">
                  <Sparkles className={isDark ? "h-5 w-5 text-amber-300" : "h-5 w-5 text-amber-500"} />
                  <div>
                    <p className={isDark ? "text-sm font-semibold uppercase tracking-label text-amber-200" : "text-sm font-semibold uppercase tracking-label text-amber-600"}>
                      {t("support.kicker")}
                    </p>
                    <h3 className={isDark ? "text-xl font-bold text-white" : "text-xl font-bold text-slate-900"}>{t("support.title")}</h3>
                  </div>
                </div>
                <p className={isDark ? "text-slate-100/85" : "text-slate-700"}>{t("support.description")}</p>
                <Button
                  asChild
                  className="w-full bg-gradient-to-r from-amber-400 to-orange-500 text-black shadow-[0_15px_40px_rgba(244,178,58,0.35)] hover:shadow-[0_18px_46px_rgba(240,124,58,0.4)] normal-case not-italic font-semibold tracking-tight"
                >
                  <a href="mailto:legal@geesol.com">{t("support.contactCta")}</a>
                </Button>
              </CardContent>
            </Card>
            <Card className={cardClass()}>
              <CardContent className={`space-y-3 p-6 md:p-7 ${isDark ? "" : "text-slate-800"}`}>
                <div className="flex items-center gap-3">
                  <ExternalLink className={isDark ? "h-5 w-5 text-amber-300" : "h-5 w-5 text-amber-500"} />
                  <div>
                    <p className={isDark ? "text-sm font-semibold uppercase tracking-label text-amber-200" : "text-sm font-semibold uppercase tracking-label text-amber-600"}>
                      {t("developerCredit.title")}
                    </p>
                    <p className={isDark ? "text-sm text-slate-100/85" : "text-sm text-slate-700"}>
                      {t("developerCredit.text")} {" "}
                      <a
                        href="https://ordinaly.ai"
                        target="_blank"
                        rel="noreferrer"
                        className={isDark ? "font-semibold text-amber-200 underline" : "font-semibold text-amber-700 underline"}
                      >
                        {t("developerCredit.linkLabel")}
                      </a>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            {/* Cookie quick settings: syncs with cookie modal via custom events */}
            <Card className={cardClass()}>
              <CardContent className={`space-y-4 p-6 md:p-7 ${isDark ? "" : "text-slate-800"}`}>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                  <div className="flex items-start gap-6 w-full">
                    <div className="w-full">
                      <p className={isDark ? "text-sm font-semibold uppercase tracking-eyebrow text-amber-200" : "text-sm font-semibold uppercase tracking-eyebrow text-amber-600"}>{t("tabs.cookies")}</p>
                      <h4 className={isDark ? "text-lg font-bold text-white mt-1" : "text-lg font-bold text-slate-900 mt-1"}>{t("cookieSettings.title")}</h4>
                      <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
                        <div className="flex items-center w-full sm:w-auto">
                          <div className="mr-3 text-sm">{t("cookieToggles.analytics")}</div>
                          <Slider
                            aria-label={t("cookieSettings.analyticsAriaLabel")}
                            checked={analyticsEnabled}
                            onChange={() => {
                              try {
                                const raw = localStorage.getItem('cookie-preferences');
                                const parsed = raw ? JSON.parse(raw) : { necessary: true, analytics: false };
                                const updated = { ...parsed, analytics: !analyticsEnabled };
                                localStorage.setItem('cookie-preferences', JSON.stringify(updated));
                                setAnalyticsEnabled(!analyticsEnabled);
                                window.dispatchEvent(new CustomEvent('cookie-preferences-changed', { detail: updated }));
                              } catch {
                                // ignore
                              }
                            }}
                          />
                        </div>

                        <div className="flex items-center w-full sm:w-auto">
                          <div className="mr-3 text-sm">{t("cookieToggles.functional")}</div>
                          <Slider
                            aria-label={t("cookieSettings.functionalAriaLabel")}
                            checked={functionalEnabled}
                            onChange={() => {
                              try {
                                const raw = localStorage.getItem('cookie-preferences');
                                const parsed = raw ? JSON.parse(raw) : { necessary: true, functional: false };
                                const updated = { ...parsed, functional: !functionalEnabled };
                                localStorage.setItem('cookie-preferences', JSON.stringify(updated));
                                setFunctionalEnabled(!functionalEnabled);
                                window.dispatchEvent(new CustomEvent('cookie-preferences-changed', { detail: updated }));
                              } catch {
                                // ignore
                              }
                            }}
                          />
                        </div>

                        <div className="flex items-center w-full sm:w-auto">
                          <div className="mr-3 text-sm">{t("cookieToggles.thirdParty")}</div>
                          <Slider
                            aria-label={t("cookieSettings.thirdPartyAriaLabel")}
                            checked={marketingEnabled}
                            onChange={() => {
                              try {
                                const raw = localStorage.getItem('cookie-preferences');
                                const parsed = raw ? JSON.parse(raw) : { necessary: true, marketing: false };
                                const updated = { ...parsed, marketing: !marketingEnabled };
                                localStorage.setItem('cookie-preferences', JSON.stringify(updated));
                                setMarketingEnabled(!marketingEnabled);
                                window.dispatchEvent(new CustomEvent('cookie-preferences-changed', { detail: updated }));
                              } catch {
                                // ignore
                              }
                            }}
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-4 x-sm:gap-6 mt-4">
                        <Button
                          onClick={() => window.dispatchEvent(new CustomEvent('open-cookie-popup', { detail: { settings: true } }))}
                          asChild
                          className="w-full bg-gradient-to-r from-amber-400 to-orange-500 text-black shadow-[0_15px_40px_rgba(244,178,58,0.35)] hover:shadow-[0_18px_46px_rgba(240,124,58,0.4)] normal-case not-italic font-semibold tracking-tight"
                        >
                          <a href="mailto:legal@geesol.com">{t("cookieSettings.buttonLabel")}</a>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
                <p className={isDark ? "text-slate-100/85" : "text-slate-700"}>{t("cookieSettings.description")}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalPage;
